import { openDB, DBSchema, IDBPDatabase } from 'idb';
import type { Config, Settings, SearchResponse, CartSession } from './types';

interface TCGDatabase extends DBSchema {
  config: {
    key: string;
    value: Config | string | Settings;
  };
  searchCache: {
    key: string;
    value: {
      query: string;
      response: SearchResponse;
      timestamp: number;
    };
    indexes: { 'by-timestamp': number };
  };
  cartSessions: {
    key: string;
    value: CartSession;
    indexes: { 'by-status': string; 'by-createdAt': string };
  };
}

const DB_NAME = 'tcg-price-lookup';
const DB_VERSION = 2;
const CACHE_TTL = 24 * 60 * 60 * 1000; // 24 hours

// Increment this to invalidate all cached search results (e.g., after API changes)
// v3: Smart search with structured queries (name, number, set_id)
const CACHE_VERSION = 3;

let dbPromise: Promise<IDBPDatabase<TCGDatabase>> | null = null;

async function getDB(): Promise<IDBPDatabase<TCGDatabase>> {
  if (!dbPromise) {
    dbPromise = openDB<TCGDatabase>(DB_NAME, DB_VERSION, {
      upgrade(db, oldVersion) {
        if (oldVersion < 1) {
          // Config store
          if (!db.objectStoreNames.contains('config')) {
            db.createObjectStore('config');
          }
          // Search cache store
          if (!db.objectStoreNames.contains('searchCache')) {
            const store = db.createObjectStore('searchCache', { keyPath: 'query' });
            store.createIndex('by-timestamp', 'timestamp');
          }
        }
        if (oldVersion < 2) {
          // Cart sessions store
          if (!db.objectStoreNames.contains('cartSessions')) {
            const store = db.createObjectStore('cartSessions', { keyPath: 'id' });
            store.createIndex('by-status', 'status');
            store.createIndex('by-createdAt', 'createdAt');
          }
        }
      },
    });
  }
  return dbPromise;
}

// Config/Token storage
export async function getToken(): Promise<string | null> {
  try {
    const db = await getDB();
    const result = await db.get('config', 'token');
    const token = typeof result === 'string' ? result : null;
    if (!token) {
      console.log('getToken: No token found in IndexedDB');
    }
    return token;
  } catch (error) {
    console.error('getToken error:', error);
    return null;
  }
}

export async function setToken(token: string): Promise<void> {
  try {
    const db = await getDB();
    await db.put('config', token, 'token');
    console.log('setToken: Token saved to IndexedDB');
  } catch (error) {
    console.error('setToken error:', error);
    throw error;
  }
}

export async function clearToken(): Promise<void> {
  try {
    const db = await getDB();
    await db.delete('config', 'token');
    console.log('clearToken: Token removed from IndexedDB');
  } catch (error) {
    console.error('clearToken error:', error);
  }
}

// Settings storage
const DEFAULT_SETTINGS: Settings = {
  buyOfferPercent: 60,
  // 'buy' is the right default for TCG events: most counter interactions are
  // either price-checks (mode-agnostic, just open the card detail) or
  // customer-selling-to-us (buy mode + cart). Operators who want sell mode
  // can toggle on demand.
  defaultMode: 'buy',
};

export async function getSettings(): Promise<Settings> {
  const db = await getDB();
  const result = await db.get('config', 'settings');
  if (result && typeof result === 'object' && 'buyOfferPercent' in result) {
    return result as Settings;
  }
  return DEFAULT_SETTINGS;
}

export async function setSettings(settings: Settings): Promise<void> {
  const db = await getDB();
  await db.put('config', settings, 'settings');
}

// Search cache
// Cache key includes version to auto-invalidate when CACHE_VERSION changes
function getCacheKey(query: string): string {
  return `v${CACHE_VERSION}:${query.toLowerCase()}`;
}

/**
 * Build a structured cache key for smart search queries.
 * Format: v3:struct:name=X|num=Y|set=Z
 */
export function getStructuredCacheKey(
  name: string,
  number?: string,
  setId?: string
): string {
  const parts = [`name=${name.toLowerCase()}`];
  if (number) {
    parts.push(`num=${number}`);
  }
  if (setId) {
    parts.push(`set=${setId}`);
  }
  return `v${CACHE_VERSION}:struct:${parts.join('|')}`;
}

export async function getCachedSearch(query: string): Promise<SearchResponse | null> {
  const db = await getDB();
  const cacheKey = getCacheKey(query);
  const result = await db.get('searchCache', cacheKey);

  if (!result) return null;

  // Check if cache is expired
  if (Date.now() - result.timestamp > CACHE_TTL) {
    await db.delete('searchCache', cacheKey);
    return null;
  }

  return result.response;
}

/**
 * Extended cache result for stale-while-revalidate pattern.
 * Unlike getCachedSearch, this doesn't delete expired entries - allowing
 * stale data to be shown while revalidating in the background.
 */
export interface CachedSearchResult {
  response: SearchResponse;
  timestamp: number;
  isExpired: boolean;
}

/**
 * Get cached search with timestamp information for stale-while-revalidate.
 * Returns expired entries (unlike getCachedSearch) to allow showing stale
 * data when offline or experiencing network issues.
 */
export async function getCachedSearchWithTimestamp(query: string): Promise<CachedSearchResult | null> {
  const db = await getDB();
  const cacheKey = getCacheKey(query);
  const result = await db.get('searchCache', cacheKey);

  if (!result) return null;

  const isExpired = Date.now() - result.timestamp > CACHE_TTL;

  return {
    response: result.response,
    timestamp: result.timestamp,
    isExpired,
  };
}

export async function setCachedSearch(query: string, response: SearchResponse): Promise<void> {
  const db = await getDB();
  const cacheKey = getCacheKey(query);
  await db.put('searchCache', {
    query: cacheKey,
    response,
    timestamp: Date.now(),
  });
}

// Cleanup old cache entries
export async function cleanupCache(): Promise<void> {
  const db = await getDB();
  const tx = db.transaction('searchCache', 'readwrite');
  const store = tx.objectStore('searchCache');
  const index = store.index('by-timestamp');

  const cutoff = Date.now() - CACHE_TTL;
  let cursor = await index.openCursor(IDBKeyRange.upperBound(cutoff));

  while (cursor) {
    await cursor.delete();
    cursor = await cursor.continue();
  }
}

// Cart session storage
export async function saveCartSession(session: CartSession): Promise<void> {
  const db = await getDB();
  await db.put('cartSessions', session);
}

export async function getCartSession(id: string): Promise<CartSession | undefined> {
  const db = await getDB();
  return db.get('cartSessions', id);
}

export async function getActiveAndParkedSessions(): Promise<CartSession[]> {
  const db = await getDB();
  const all = await db.getAll('cartSessions');
  return all.filter((s) => s.status === 'active' || s.status === 'parked');
}

export async function getCompletedSessions(): Promise<CartSession[]> {
  const db = await getDB();
  const all = await db.getAllFromIndex('cartSessions', 'by-createdAt');
  return all.filter((s) => s.status === 'completed').reverse();
}

export async function deleteCartSession(id: string): Promise<void> {
  const db = await getDB();
  await db.delete('cartSessions', id);
}

export async function clearCompletedSessions(): Promise<void> {
  const db = await getDB();
  const all = await db.getAll('cartSessions');
  const tx = db.transaction('cartSessions', 'readwrite');
  for (const session of all) {
    if (session.status === 'completed') {
      await tx.store.delete(session.id);
    }
  }
  await tx.done;
}

// Clear all data (for logout)
export async function clearAllData(): Promise<void> {
  const db = await getDB();
  await db.clear('config');
  await db.clear('searchCache');
  await db.clear('cartSessions');
}
